import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgSelectConfig } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { FB_PIXEL_KEY, FIREBASE_URL_FUNCTIONS, GTAG_KEY, HELPER_EMPLOYEE_ID_TARGET_STORAGE_KEY, LANG_KEY_LOCAL_STORAGE_KEY } from './app.constants';
import { AuthFirebaseService } from './core/services/auth-firebase.service';
import { FireLoggingService } from './core/services/fire-logging.service';
import { LanguageService } from './core/services/language.service';
import { StorageService } from './core/services/storage.service';
import { UserService } from './core/services/user.service';
import { CookieDialogComponent } from './shared/components/cookie-dialog/cookie-dialog.component';
import { CookieTypesEnum } from './shared/enums/cookie-types.enum';
import { AppService } from './shared/services/app.service';
import { PixelService } from './shared/services/pixel.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
    private defaulLanguage = 'de';
    
    constructor(
        private translate: TranslateService,
        private languageService: LanguageService,
        private storageService: StorageService,
        private ngSelectConfig: NgSelectConfig,
        private appService: AppService,
        private dialog: MatDialog,
        private pixelService: PixelService,
        private http: HttpClient,
        private userService: UserService,
        private auth: AuthFirebaseService,
        private fireLoggingService: FireLoggingService,
    ) { }
    
    ngOnInit(): void {
        this.configTranslate();
        this.configSelectTranslate();
        this.configGtag();
        this.configPixel();
        this.setUserSessionIfLoggedIn();
        const helperEmployeeIdTarget = this.storageService.get(HELPER_EMPLOYEE_ID_TARGET_STORAGE_KEY);
        this.userService.operatedByHelperUser = !!helperEmployeeIdTarget;
    }
    
    ngAfterViewInit(): void {
        setTimeout(() => {
            window.document.getElementById('finstien-loading')?.remove();
        }, 500);
        if (!this.appService.getCookiesAllowed()) {
            timer(1000).subscribe(() => {
                this.openCookieDialog();
            });
        }
    }
    
    private configTranslate() {
        const allowedCookie = this.appService.isCookieIsAllowed(CookieTypesEnum.PREFERENCES);
        let browserLang = this.storageService.get(LANG_KEY_LOCAL_STORAGE_KEY);
        if (!browserLang) {
            browserLang = this.defaulLanguage;
            if (allowedCookie) {
                this.storageService.save(LANG_KEY_LOCAL_STORAGE_KEY, this.defaulLanguage);
            }
        }
        browserLang = browserLang.match(/en|de/) ? browserLang : this.defaulLanguage;
        this.languageService.changeLanguage(browserLang);
    }
    
    private configSelectTranslate() {
        timer(0).subscribe(() => {
            this.translate
            .get('system.empty-list')
            .subscribe((result: string) => {
                this.ngSelectConfig.notFoundText = result;
            });
            this.translate
            .get('system.type-to-search')
            .subscribe((result: string) => {
                this.ngSelectConfig.typeToSearchText = result;
            });
        });
    }
    
    private configGtag() {
        if (this.appService.isCookieIsAllowed(CookieTypesEnum.ANALYTICS)) {
            (window as any).gtag('js', new Date());
            (window as any).gtag('config', GTAG_KEY);
        }
    }
    
    private configPixel() {
        if (this.appService.isCookieIsAllowed(CookieTypesEnum.ANALYTICS)) {
            this.pixelService.init(FB_PIXEL_KEY);
        }
    }
    
    openCookieDialog() {
        this.dialog.open(CookieDialogComponent, {
            panelClass: 'simple-modal',
            width: '600px',
            height: 'auto',
            maxHeight: '95vh',
            data: {
                cookieTypes: [CookieTypesEnum.NECESSARY,
                    CookieTypesEnum.PREFERENCES,
                    CookieTypesEnum.ANALYTICS
                ]
            }
        });
    }
    
    private setUserSessionIfLoggedIn() {
        if (this.userService.loggedUser) {
            this.http.get(`${FIREBASE_URL_FUNCTIONS}getClientIp`)
            .pipe(
                catchError(err => {
                    this.fireLoggingService.sendAlertLog(
                        `Error when trying setUserSessionIfLoggedIn for user ${this.userService.loggedUser.email}. Details: ${err.message}`
                    );
                    return of(null);
                }),
                map((response: any) => {
                    if (response) {
                        return {
                            ip: response.ip,
                            createdAt: new Date(),
                            userAgent: navigator.userAgent
                        };
                    }
                    return null;
                }),
                tap((data) => {
                    this.userService.browserClientData = data!!;
                    return data;
                }),
                switchMap(() => {
                    return this.userService.isLogged();
                }),
                map((isLoggedIn) => {
                    if (isLoggedIn) {
                        this.auth.setUserSession(this.userService.loggedUser);
                        this.auth.listenUserSessionChanged(this.userService.loggedUser);
                    }
                    return isLoggedIn;
                })
            ).subscribe();
        }
    }    
}
